import {createElement, ReactNode} from 'react';
import {div, hbox, span} from '@xh/hoist/cmp/layout';
import {StoreRecord} from '@xh/hoist/data';
import {fmtNumber, fmtPercent} from '@xh/hoist/format';
import {isEmpty, max, min} from 'lodash';

/**
 * Cell renderers for the expanded watchlist grid. They build plain divs and inline SVG rather than
 * input components, so they stay cheap to re-render on every live tick.
 */

/** Net change over percent change, for a two-line solid change block. */
export function changeBlockRenderer(v: number, {record}: {record: StoreRecord}): ReactNode {
    if (v == null) return null;
    const {priceChangePct} = record.data,
        glyph = v > 0 ? '▲' : v < 0 ? '▼' : '';

    return hbox({
        className: 'wl-change',
        items: [
            span({className: 'wl-change__glyph', item: glyph}),
            div({
                className: 'wl-change__values',
                items: [
                    div(fmtNumber(v, {precision: 2, withPlusSign: true}) as string),
                    div(fmtPercent(priceChangePct, {precision: 2, withPlusSign: true}) as string)
                ]
            })
        ]
    });
}

/** Thin low-high bar with a marker where the last price sits, for the low and high in the named fields. */
export function rangeRenderer(lowField: string, highField: string) {
    return (v: number, {record}: {record: StoreRecord}): ReactNode => {
        const {[lowField]: low, [highField]: high, price} = record.data;
        return low == null || high == null ? null : rangeTrack(low, high, price);
    };
}

export const dayRangeRenderer = rangeRenderer('dayLow', 'dayHigh');
export const yearRangeRenderer = rangeRenderer('yearLow', 'yearHigh');

function rangeTrack(low: number, high: number, price: number): ReactNode {
    const rangeWidth = high - low,
        pos = rangeWidth > 0 ? Math.min(Math.max((price - low) / rangeWidth, 0), 1) : 0.5;

    return div({
        className: 'wl-range',
        items: [
            hbox({
                className: 'wl-range__labels',
                items: [span(fmtNumber(low, {precision: 2}) as string), span(fmtNumber(high, {precision: 2}) as string)]
            }),
            div({
                className: 'wl-range__track',
                items: [
                    div({className: 'wl-range__fill', style: {width: `${pos * 100}%`}}),
                    div({className: 'wl-range__marker', style: {left: `${pos * 100}%`}})
                ]
            })
        ]
    });
}

/** Relative volume as a number with an inline bar, capped at 3x. */
export function relVolumeRenderer(v: number): ReactNode {
    if (v == null) return null;
    return hbox({
        className: v >= 1 ? 'wl-rvol wl-rvol--hot' : 'wl-rvol',
        items: [
            div({className: 'wl-rvol__track', item: div({className: 'wl-rvol__bar', style: {width: `${(Math.min(v, 3) / 3) * 100}%`}})}),
            span({className: 'wl-rvol__value', item: v.toFixed(2)})
        ]
    });
}

const SPARK_W = 150,
    SPARK_H = 20,
    SPARK_PLOT_W = 112,
    SPARK_PAD = 2;

/**
 * Price path sparkline in inline SVG, colored by direction. It has a dashed prior-close reference
 * line and the path's high and low labeled at the right edge. With `slots`, the x axis spans that many
 * points, so a series still filling in covers only the part of the plot it has reached.
 */
export function sparklineRenderer(history: number[], slots: number = null): ReactNode {
    if (isEmpty(history) || history.length < 2) return null;

    const lo = min(history),
        hi = max(history),
        range = hi - lo || 1,
        prevClose = history[0],
        isUp = history[history.length - 1] >= prevClose,
        span = Math.max(slots ?? 0, history.length) - 1,
        x = (i: number) => (i / span) * SPARK_PLOT_W,
        y = (v: number) => SPARK_PAD + (1 - (v - lo) / range) * (SPARK_H - 2 * SPARK_PAD),
        line = history.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' '),
        area = `0,${SPARK_H} ${line} ${x(history.length - 1).toFixed(1)},${SPARK_H}`;

    return createElement(
        'svg',
        {className: isUp ? 'wl-spark wl-spark--up' : 'wl-spark wl-spark--down', width: SPARK_W, height: SPARK_H, viewBox: `0 0 ${SPARK_W} ${SPARK_H}`},
        createElement('polygon', {className: 'wl-spark__area', points: area}),
        createElement('line', {className: 'wl-spark__ref', x1: 0, x2: SPARK_PLOT_W, y1: y(prevClose), y2: y(prevClose)}),
        createElement('polyline', {className: 'wl-spark__line', points: line}),
        createElement('text', {className: 'wl-spark__label', x: SPARK_PLOT_W + 4, y: 8}, fmtNumber(hi, {precision: 2}) as string),
        createElement('text', {className: 'wl-spark__label', x: SPARK_PLOT_W + 4, y: SPARK_H - 2}, fmtNumber(lo, {precision: 2}) as string)
    );
}
